import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChevronRight,
  Clock3,
  FileText,
  Filter,
  Layers3,
  Library as LibraryIcon,
  Search,
  Sparkles,
} from "lucide-react";
import { PageHeader } from "@/components/shell/PageHeader";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/badges/StatusBadge";
import { PlatformBadge } from "@/components/badges/PlatformBadge";
import { useAppState } from "@/state/AppStateProvider";
import { ASSET_TYPE_LABELS, CONTENT_STATUSES, PLATFORMS, PLATFORM_LABELS, STATUS_LABELS } from "@/lib/constants";
import { buildLibraryWorkspace } from "@/lib/libraryWorkspace";
import { cn, timeAgo, formatDateTime, truncate } from "@/lib/utils";

export default function LibraryPage() {
  const { state } = useAppState();
  const [tab, setTab] = useState("all");
  const [platform, setPlatform] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");

  const workspace = useMemo(() => (
    buildLibraryWorkspace({
      assets: state.assets,
      runs: state.runs,
      filters: { tab, platform, status, query: q },
    })
  ), [state.assets, state.runs, tab, platform, status, q]);

  const filtered = workspace.filtered;

  const onTabChange = (value) => {
    setTab(value);
    setStatus("all");
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-6 pb-20" data-testid="library-page">
      <PageHeader
        eyebrow="Library"
        title="Every content activity, organized by the next move."
        description="Generated posts, research notes, approvals, scheduled content, and published records stay together without feeling like a spreadsheet."
        testId="library-page-header"
      />

      <section className="rounded-xl border bg-card p-3 inner-highlight" style={{ borderColor: "var(--border-subtle)" }}>
        <div className="flex flex-wrap items-center gap-3">
          <Tabs value={tab} onValueChange={onTabChange}>
            <TabsList className="flex h-auto flex-wrap justify-start gap-1 bg-transparent p-0" data-testid="library-tabs">
              {workspace.tabs.map((item) => (
                <TabsTrigger
                  key={item.id}
                  value={item.id}
                  className="gap-1.5 data-[state=active]:bg-[color:var(--surface-3)] data-[state=active]:text-foreground data-[state=active]:shadow-sm"
                  data-testid={`library-tab-${item.id}`}
                >
                  {item.label}
                  <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">{item.count}</span>
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-3 border-t pt-3" style={{ borderColor: "var(--border-subtle)" }}>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Filter className="h-3.5 w-3.5" />
            Filters
          </div>
          <Select value={platform} onValueChange={setPlatform}>
            <SelectTrigger className="h-9 w-full sm:w-[170px]" style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }} data-testid="library-platform-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All platforms</SelectItem>
              {PLATFORMS.map((item) => (
                <SelectItem key={item} value={item}>{PLATFORM_LABELS[item]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-9 w-full sm:w-[170px]" style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }} data-testid="library-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {CONTENT_STATUSES.map((item) => (
                <SelectItem key={item} value={item}>{STATUS_LABELS[item]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative w-full sm:ml-auto sm:max-w-sm">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(event) => setQ(event.target.value)}
              placeholder="Search titles, hooks, skills..."
              className="h-9 pl-8"
              data-testid="library-search"
              style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }}
            />
          </div>
        </div>
      </section>

      {filtered.length === 0 ? (
        state.assets.length === 0 ? (
          <EmptyState
            icon={Sparkles}
            title="No content yet"
            description="Generate your first drafts and they'll land here for review, approval, and scheduling."
            action={<Button asChild className="gap-1.5" data-testid="library-empty-create"><Link to="/create"><Sparkles className="h-4 w-4" /> Create your first post</Link></Button>}
          />
        ) : (
          <EmptyState icon={LibraryIcon} title="No content matches" description="Try clearing filters or searching for a different term." />
        )
      ) : (
        <section data-testid="library-list">
          <div className="mb-3 flex items-baseline gap-2 px-1">
            <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
              {filtered.length} {filtered.length === 1 ? "activity" : "activities"}
            </p>
            <span aria-hidden className="text-muted-foreground/50">·</span>
            <p className="text-[12px] text-muted-foreground">Sorted by the work most likely to need attention.</p>
          </div>

          <div className="grid gap-3">
            {filtered.map((asset) => (
              <LibraryAssetCard key={asset.id} asset={asset} />
            ))}
          </div>
        </section>
      )}
    </motion.div>
  );
}

function LibraryAssetCard({ asset }) {
  const preview = truncate([asset.hook, asset.body].filter(Boolean).join(" "), 180) || "No preview copy yet.";
  const typeLabel = ASSET_TYPE_LABELS[asset.asset_type] || asset.asset_type;
  const scheduledLabel = asset.schedule_at ? formatDateTime(asset.schedule_at) : null;
  const action = asset.libraryAction || { label: "Open", hint: "Open this asset.", tone: "neutral" };
  const actionClasses = {
    review: "border-[color:var(--accent-amber)]/30 bg-[color:var(--accent-amber)]/12 text-[color:var(--accent-amber)]",
    approved: "border-[color:var(--accent-green)]/30 bg-[color:var(--accent-green)]/12 text-[color:var(--accent-green)]",
    scheduled: "border-[color:var(--accent-blue)]/35 bg-[color:var(--accent-blue)]/14 text-[color:var(--accent-cyan)]",
    published: "border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] text-[color:var(--text-2)]",
    archived: "border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] text-[color:var(--text-3)]",
    neutral: "border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] text-[color:var(--text-2)]",
  };

  return (
    <Link
      to={`/library/${asset.id}`}
      className="group block rounded-xl border bg-card p-4 inner-highlight transition hover:-translate-y-0.5 hover:border-[color:var(--accent-blue)]/40 hover:shadow-[0_8px_20px_rgba(12, 15, 14,0.5)]"
      style={{ borderColor: "var(--border-subtle)" }}
      data-testid={`library-row-${asset.id}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <PlatformBadge platform={asset.platform} compact />
        <StatusBadge status={asset.status} />
        <span className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[10.5px] uppercase tracking-[0.14em] text-muted-foreground" style={{ borderColor: "var(--border-subtle)" }}>
          {asset.isSupporting ? <FileText className="h-3 w-3" /> : <Layers3 className="h-3 w-3" />}
          {typeLabel}
        </span>
      </div>

      <div className="mt-3 grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px] lg:items-center">
        <div className="min-w-0">
          <h3 className="line-clamp-1 text-[15px] font-semibold tracking-[-0.01em] text-1">{asset.displayTitle || asset.title}</h3>
          <p className="mt-1.5 line-clamp-2 text-[13px] leading-relaxed text-muted-foreground">{preview}</p>
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] text-muted-foreground">
            {asset.previewMeta?.characterLabel && (
              <span className={cn("inline-flex items-center gap-1", asset.previewMeta.overLimit && "font-medium text-[color:var(--accent-amber)]")}>
                {asset.previewMeta.characterLabel}{asset.previewMeta.overLimit ? " · over limit" : ""}
              </span>
            )}
            <span aria-hidden>·</span>
            <span>Created {timeAgo(asset.created_at || asset.updated_at)}</span>
            {asset.updated_at && asset.created_at && asset.updated_at !== asset.created_at && (
              <>
                <span aria-hidden>·</span>
                <span>edited {timeAgo(asset.updated_at)}</span>
              </>
            )}
            {scheduledLabel && (
              <>
                <span aria-hidden>·</span>
                <span className="inline-flex items-center gap-1 text-[color:var(--accent-cyan)]"><Clock3 className="h-3 w-3" />Publishes {scheduledLabel}</span>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 rounded-lg border px-3 py-2.5" style={{ borderColor: "var(--border-subtle)" }}>
          <div className="min-w-0">
            <span className={cn("inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold", actionClasses[action.tone] || actionClasses.neutral)}>
              {action.label}
            </span>
            <p className="mt-1.5 line-clamp-2 text-[11px] leading-relaxed text-muted-foreground">{action.hint}</p>
          </div>
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5" />
        </div>
      </div>
    </Link>
  );
}

